import {
  useState,
  useEffect,
  memo,
  useRef,
  type PropsWithChildren,
} from "react";
import { type Root, createRoot } from "react-dom/client";
import mapboxgl from "mapbox-gl";

type AdvancedMarkerProps = {
  onClick?: (event: mapboxgl.MapMouseEvent) => void;
  map?: mapboxgl.Map;
  position?: [number, number]; // [lng, lat]
  zIndex?: number;
};

const AdvancedMarker: React.FC<PropsWithChildren<AdvancedMarkerProps>> = ({
  onClick,
  map,
  children,
  position,
  zIndex,
}) => {
  const rootRef = useRef<Root | null>(null);
  const [marker, setMarker] = useState<mapboxgl.Marker>();

  useEffect(() => {
    if (!map || !position) return;

    if (!marker) {
      const container = document.createElement("div");
      container.style.cursor = onClick ? "pointer" : "default";
      if (zIndex !== undefined) {
        container.style.zIndex = zIndex.toString();
      }
      
      rootRef.current = createRoot(container);

      const newMarker = new mapboxgl.Marker({
        element: container,
      })
        .setLngLat(position)
        .addTo(map);

      setMarker(newMarker);
    }

    return () => {
      if (marker) {
        marker.remove();
      }
      if (rootRef.current) {
        rootRef.current.unmount();
        rootRef.current = null;
      }
    };
  }, [marker, map, position, onClick, zIndex]);

  useEffect(() => {
    if (marker && position) {
      marker.setLngLat(position);
    }
  }, [marker, position]);

  useEffect(() => {
    if (marker && zIndex !== undefined) {
      const element = marker.getElement();
      element.style.zIndex = zIndex.toString();
    }
  }, [marker, zIndex]);

  useEffect(() => {
    if (!marker) return;

    rootRef?.current?.render(children);
    
    if (onClick) {
      const element = marker.getElement();
      element.addEventListener("click", onClick as any);
      
      return () => {
        element.removeEventListener("click", onClick as any);
      };
    }
  }, [marker, children, onClick]);

  return null;
};

export default memo(AdvancedMarker);
